/*eslint-env browser*/
import addEventCapabilities from "./event_capabilities_queued.js"
import Screen from "./screen.js"

const proxyToAllLayers = [
    "updateCanvasSize",
    "clear",
    "setCamera",
    "setScale",
    "invertY",
    "multiplyScale",
    "setCenter",
    "translateCenter",
    "setOrientation",
    "rotate",
    "usePixelCoordinates"
]


function simpleValue (args) {
    if (typeof args[0] === "object") {
        return [args[0].value]
    } else {
        return args
    }
}





const argTranslators = {
    setScale: function (args) {
        if (typeof args[0] === "object") {
            return [args[0].scale]
        } else {
            return args
        }
    },
    multiplyScale: simpleValue,
    invertY:       simpleValue,
    setOrientation: function (args) {
        if (typeof args[0] === "object") {
            return [args[0].orientation]
        } else {
            return args
        }
    }
}



const defaultsModifiers = {
    setScale: function (scale) {
        this.scale = scale
    },
    multiplyScale: function (multiplier) {
        this.scale *= multiplier
    },
    invertY: function (inversion) {
        this.yDir = inversion ? -1 : 1
    },
    setCenter: function ({x, y}) {
        this.center = {x, y}
    },
    setCamera: function ({center, scale, orientation = null}) {
        if (center) {
            let {x, y} = center
            this.center = {x, y}
        }
        if (scale) {
            this.scale = scale
        }
        if (orientation !== null) {
            this.orientation = orientation
        }
    },
    translateCenter: function ({x, y}) {
        this.center.x += x
        this.center.y += y
    },
    setOrientation: function (orientation) {
        this.orientation = orientation
    },
    rotate: function (angle) {
        this.orientation += angle
    }
}


let proxyMethodsAdded = false
function createLayerProxyMethods () {
    for (let method in Screen.prototype) {
        if (LayeredScreen.prototype[method]) {
            continue
        }
        if (proxyToAllLayers.indexOf(method) !== -1) {
            createLayerProxyMethod(method, true)
        } else if (Screen.prototype.hasOwnProperty(method)) {
            createLayerProxyMethod(method)
        }
    }

    createLayerProxyMethod("coordsToPixelCoords")
    createLayerProxyMethod("pixelCoordsToCoords")
    proxyMethodsAdded = true
}


function createLayerProxyMethod (method, defaultToAll) {

    function applyOnScreen (layeredScreen, layerNumber, args) {
        const screen = (layeredScreen.layers[layerNumber] || addLayer(layeredScreen, layerNumber))
        return screen[method](...args)
    }

    const argTranslator = argTranslators[method]

    if (defaultToAll) {
        const applyToAll = createApplyToAll(method)
        LayeredScreen.prototype[method] = function (...args) {
            const layerNumber = args[0] && args[0].layer
            if (typeof layerNumber === "number") {
                return applyOnScreen(this, layerNumber, (argTranslator ? argTranslator(args) : args))
            } else {
                return applyToAll.apply(this, args)
            }
        }
    } else {
        LayeredScreen.prototype[method] = function (...args) {
            const layerNumber = args[0] && args[0].layer
            return applyOnScreen(this, layerNumber || 0, (argTranslator ? argTranslator(args) : args))
        }
    }

}


function createApplyToAll (method) {

    const defaultsModifier = defaultsModifiers[method]

    function applyToAll (...args) {
        if (defaultsModifier) {
            defaultsModifier.apply(this, args)
        }
        for (let number in this.layers) {
            const screen = this.layers[number]
            screen[method](...args)
        }
    }
    return applyToAll
}










function LayeredScreen (domContainer, {crispCanvas, errorHandler} = {}) {
    if (!proxyMethodsAdded) {
        createLayerProxyMethods()
    }

    //Support for jquery element
    if (Object.getPrototypeOf(domContainer).jquery) {
        domContainer = domContainer[0]
    }

    if (!domContainer) {
        throw new Error("LayeredScreen must be given a block container.")
    }

    if (domContainer.tagName === "CANVAS") {
        throw new Error("LayeredScreen must be given a block (typically a DIV), not a canvas.")
    }

    this.errorHandler = errorHandler

    this.crispCanvas = crispCanvas
    this.container   = domContainer
    this.resize()

    setContainerPosition(domContainer)

    this.layers = {}

    this.initiators = []

    setDefaults(this)

    this.internalEventBus = {}
    addEventCapabilities(this.internalEventBus)

    addLayer(this, 0)
}


LayeredScreen.prototype.forEachScreen = function (iterator) {
    for (let number in this.layers) {
        const screen = this.layers[number]
        iterator(screen, number)
    }
}


LayeredScreen.prototype.initForEachScreen = function (init) {
    this.forEachScreen(init)
    this.initiators.push(init)
}


LayeredScreen.prototype.resize = function () {
    this.width  = this.container.clientWidth
    this.height = this.container.clientHeight
    for (let number in this.layers) {
        setCanvasSize(this.layers[number].canvas, this.width, this.height, this.crispCanvas)
    }
    this.updateCanvasSize()
}



LayeredScreen.prototype.setOptimalCamera = Screen.prototype.setOptimalCamera //special case
LayeredScreen.prototype.setOptimalView = Screen.prototype.setOptimalView  //FIXME remove




function setContainerPosition (container) {
    let style = container.currentStyle || window.getComputedStyle(container, null)
    if (style.position !== "absolute" && style.position !== "relative") {
        container.style.position = "relative"
    }
}


function setDefaults (layeredScreen) {
    layeredScreen.scale       = 1
    layeredScreen.yDir        = 1
    layeredScreen.center      = {x: 0, y: 0}
    layeredScreen.orientation = 0
}



function addLayer (layeredScreen, zIndex) {
    if (typeof zIndex === "undefined") {
        throw new Error("Layer number must be given.")
    }

    let {layers, initiators} = layeredScreen

    layers[zIndex] = createLayer(layeredScreen, zIndex)
    layers[zIndex].zIndex = zIndex

    initiators.forEach((initiator) => initiator(layers[zIndex], zIndex))

    return layers[zIndex]
}


function createLayer (layeredScreen, zIndex) {

    const canvas = findOrCreateCanvas(layeredScreen, zIndex)

    const {crispCanvas, errorHandler} = layeredScreen

    const screen = new Screen(canvas, {crispCanvas, errorHandler})

    screen.yDir = layeredScreen.yDir
    screen.setScale(layeredScreen.scale)
    screen.setCenter(layeredScreen.center)
    screen.setOrientation(layeredScreen.orientation)

    return screen
}


function findOrCreateCanvas (layeredScreen, zIndex) {
    return findCanvas(layeredScreen, zIndex) || createCanvas(layeredScreen, zIndex)
}


function findCanvas ({container}, zIndex) {
    return container.querySelector(`canvas[data-layer='${zIndex}']`)
}


function createCanvas (layeredScreen, zIndex) {
    const {width, height, crispCanvas} = layeredScreen
    const canvas          = document.createElement("canvas")
    canvas.dataset.layer  = zIndex
    canvas.style.position = "absolute"
    canvas.style.top      = 0
    canvas.style.left     = 0

    setCanvasSize(canvas, width, height, crispCanvas)

    addCanvasToContainer({layeredScreen, canvas, zIndex})

    return canvas
}


function addCanvasToContainer ({layeredScreen, canvas, zIndex}) {
    const {container} = layeredScreen
    const aboveLayerZ = findZIndexAbove(layeredScreen, zIndex)

    if (aboveLayerZ === false) {
        container.appendChild(canvas)
    } else {
        let aboveLayer = layeredScreen.layers[aboveLayerZ]
        container.insertBefore(canvas, aboveLayer.canvas)
    }
}


function setCanvasSize (canvas, width, height, crispCanvas) {
    let realWidth  = crispCanvas ? 2 * width  : width
    let realHeight = crispCanvas ? 2 * height : height
    if (canvas.width === realWidth && canvas.height === realHeight) {
        return
    }
    canvas.width        = realWidth
    canvas.height       = realHeight
    canvas.style.width  = `${width}px`
    canvas.style.height = `${height}px`
}


function findZIndexAbove (layeredScreen, zIndex) {
    let result = Infinity
    for (let z in layeredScreen.layers) {
        z = parseInt(z, 10)
        if (z <= zIndex) {
            continue
        }
        result = Math.min(z, result)
    }
    if (result === Infinity) {
        result = false
    }
    return result
}


export default LayeredScreen

